import { useState, useEffect } from 'react'
import './App.css'
import StickyHeaderPortal from './StickyHeaderPortal'

// Phone formatting helper functions
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '').slice(0, 10);  // Limit to 10 digits
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength === 0) return '';
  if (phoneNumberLength <= 3) return `(${phoneNumber}`;
  if (phoneNumberLength <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
};

const normalizePhoneNumber = (value) => {
  if (!value) return '';
  const phoneNumber = value.replace(/[^\d]/g, '');
  if (phoneNumber.length < 10) return '';
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const isValidUSPhone = (phone) => {
  const normalized = phone.replace(/[^\d]/g, '');
  return normalized.length === 10;
};

const isValidEmail = (email) => {
  // RFC 5322 compliant email regex
  const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email ? emailRegex.test(email) : true; // Return true if empty since email is optional
};

function App() {
  const [modalOpen, setModalOpen] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 200)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Add effect to handle modal open/close
  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [modalOpen]);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFormSuccess = () => {
    setModalOpen(false);
    setShowSuccessPopup(true);
  };

  const handleDismissPopup = () => {
    setShowSuccessPopup(false);
  };

  // Service icons (emoji for MVP, can swap for SVGs)
  const services = [
    { icon: '🧠', title: 'Strategic Consulting', desc: 'Expert advice to help you leverage technology for maximum growth.' },
    { icon: '⚙️', title: 'Tools & Automation', desc: 'Streamline operations and save time with custom automation solutions.' },
    { icon: '🔗', title: 'Custom Integrations', desc: 'Connect your tools and platforms for seamless data flow and productivity.' },
    { icon: '🤖', title: 'AI Chatbots', desc: 'Automate customer interactions and support with intelligent, 24/7 chatbots tailored to your business.' },
    { icon: '📞', title: 'AI Voice Answering', desc: 'Never miss a call—let AI handle and route your voice calls professionally.' },
    { icon: '💬', title: 'Missed Call Text-Back', desc: 'Instantly follow up on missed calls with automated text messages to keep leads warm.' },
    { icon: '🔍', title: 'Search Engine Optimization', desc: 'Boost your online visibility and attract more customers with advanced search engine optimization.' },
    { icon: '📈', title: 'Social Media Marketing', desc: 'Grow your brand and engage your audience with data-driven social media strategies.' },
    { icon: '📊', title: 'Advanced Reporting', desc: 'Gain actionable insights with custom dashboards and analytics for smarter decision-making.' },
    { icon: '🌐', title: 'Web Development', desc: 'Modern, responsive websites designed to convert and impress.' },
    { icon: '🖥️', title: 'Web Hosting', desc: 'Fast, secure, and reliable hosting for your business-critical sites.' }
  ]

  return (
    <>
      <a id="top" tabIndex={-1} style={{ position: 'absolute', top: 0 }}></a>
      <h1 className="visually-hidden">Transcend Solutions - Elevating Businesses Through Automation and Digital Strategy</h1>
      <StickyHeaderPortal>
        <Header onCTAClick={() => setModalOpen(true)} />
      </StickyHeaderPortal>
      <div className="main-bg" style={{ paddingTop: '70px' }}>
        <main>
          <Hero onCTAClick={() => setModalOpen(true)} />
          <hr className="section-divider" aria-hidden="true" />
          <div id="about" style={{ position: 'relative', top: '-80px' }}></div>
          <AboutSection />
          <hr className="section-divider" aria-hidden="true" />
          <div id="services" style={{ position: 'relative', top: '-30px' }}></div>
          <section className="services-section" id="services">
            <h3>Our Services</h3>
            <div className="services-grid">
              {services.map((s, i) => (
                <ServiceCard key={s.title} icon={s.icon} title={s.title} desc={s.desc} />
              ))}
            </div>
          </section>
          <hr className="section-divider" aria-hidden="true" />
          <div id="why" style={{ position: 'relative', top: '-65px' }}></div>
          <WhyChooseUsSection />
          <hr className="section-divider" aria-hidden="true" />
          <ContactSection onCTAClick={() => setModalOpen(true)} />
        </main>
        <Footer />
        {modalOpen && <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSuccess={handleFormSuccess} />}
        {showSuccessPopup && (
          <div className="success-popup">
            <p>Message sent successfully! We'll be in touch soon.</p>
            <button onClick={handleDismissPopup}>Got it</button>
          </div>
        )}
        <button
          className={`scroll-to-top${showScrollTop ? ' visible' : ''}`}
          onClick={handleScrollTop}
          aria-label="Scroll to top"
          tabIndex={showScrollTop ? 0 : -1}
        >
          ↑
        </button>
      </div>
    </>
  )
}

function Header({ onCTAClick }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="site-header" aria-label="Main Navigation">
      <div className="header-content">
        <div className="header-logo-container">
          <img src="/logo.png" alt="Transcend Solutions - Elevating Businesses Through Automation and Digital Strategy" className="header-logo-img" />
          <a href="#top" onClick={() => setIsMobileMenuOpen(false)} className="company-name">Transcend Solutions</a>
        </div>
        <button 
          className="mobile-menu-toggle" 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          <span className="hamburger-icon"></span>
        </button>
        <div className={`header-nav${isMobileMenuOpen ? ' mobile-open' : ''}`}>
          <a href="#top" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
          <a href="#about" onClick={() => setIsMobileMenuOpen(false)}>About</a>
          <a href="#services" onClick={() => setIsMobileMenuOpen(false)}>Services</a>
          <a href="#why" onClick={() => setIsMobileMenuOpen(false)}>Why Us</a>
          <button className="cta-btn nav-cta" onClick={() => { onCTAClick(); setIsMobileMenuOpen(false); }} aria-label="Get Started">Get Started</button>
        </div>
      </div>
    </nav>
  )
}

function Hero({ onCTAClick }) {
  const fomotext = "Outpace your competition—automate smarter, scale faster";
  
  return (
    <section className="hero-section">
      <h1>Elevate Your Business with Transcend Solutions</h1>
      <h2><b>Strategic deployment of automated systems designed to boost your growth in the digital age</b></h2>
      <p className="hero-fomo" aria-label={fomotext}>
        <span>Outpace your competition—</span>
        <span>automate smarter, </span>
        <span>scale faster</span>
      </p>
      <button className="cta-btn hero-cta" onClick={onCTAClick} aria-label="Get Started">Get Started</button>
    </section>
  )
}

function ServiceCard({ icon, title, desc }) {
  return (
    <div className="service-card">
      <h4><span className="service-icon" aria-hidden="true">{icon}</span> {title}</h4>
      <p>{desc}</p>
    </div>
  )
}

function AboutSection() {
  return (
    <section className="about-section section" id="about">
      <h3>About Transcend</h3>
      <div className="about-key-points">
        <div className="about-key-item">
          <span className="about-key-icon animated-gradient-arrow">&#8250;</span>
          <span className="about-key-text">Your trusted, future-proof IT consulting partner—empowering businesses with cutting-edge automation and AI-driven solutions.</span>
        </div>
        <div className="about-key-item">
          <span className="about-key-icon animated-gradient-arrow">&#8250;</span>
          <span className="about-key-text">We specialize in designing intelligent automation systems that drive efficiency, scalability, and long-term success.</span>
        </div>
        <div className="about-key-item">
          <span className="about-key-icon animated-gradient-arrow">&#8250;</span>
          <span className="about-key-text">AI evolves daily. Staying competitive means staying ahead. Our expertise keeps businesses future-ready with seamless innovation.</span>
        </div>
      </div>
      <p className="about-mission"><em>Let's transcend limits and redefine what's possible!</em></p>
    </section>
  )
}

function WhyChooseUsSection() {
  return (
    <section className="why-section" id="why">
      <h3>Why Choose Us?</h3>
      <ul className="why-list">
        <li>✅ Strategic expertise in AI, automation, and digital marketing.</li>
        <li>✅ Proactive innovation—keeping you ahead of market trends while competitors play catch-up.</li>
        <li>✅ Clear communication and transparency—delivering results you can trust.</li>
        <li>✅ Tailored solutions built for lasting impact—from consultation to execution to ongoing optimization.</li>
      </ul>
    </section>
  )
}

function ContactSection({ onCTAClick }) {
  return (
    <section className="contact-section" id="contact">
      <h3>Ready to Transcend?</h3>
      <p>Elevate your business with cutting-edge automation and AI-driven solutions. Let's create your competitive advantage—starting today.</p>
      <p className="contact-fomo">Your competition won't wait. Why should you?</p>
      <button className="cta-btn" onClick={onCTAClick} aria-label="Get Started">Get Started</button>
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <span>© {new Date().getFullYear()} Transcend Solutions, LLC. All rights reserved.</span>
    </footer>
  )
}

const ContactModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [userInteractions, setUserInteractions] = useState({
    formEdits: 0,
    fieldFocusTimes: {},
    totalTimeSpent: 0,
    startTime: Date.now(),
    fieldSequence: [],
    editPatterns: {
      totalEdits: 0,
      fieldEdits: {},
      corrections: {}
    },
    scrollDepth: 0,
    scrollPatterns: [],
    previousPageViews: []
  });

  // Initialize page view tracking
  useEffect(() => {
    const pageViews = JSON.parse(localStorage.getItem('pageViews') || '[]');
    pageViews.push(window.location.pathname);
    localStorage.setItem('pageViews', JSON.stringify(pageViews.slice(-5)));
    
    // Track scroll depth
    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const scrollDepth = Math.round((currentScroll / maxScroll) * 100);
      
      setUserInteractions(prev => ({
        ...prev,
        scrollDepth: Math.max(prev.scrollDepth, scrollDepth),
        scrollPatterns: [...new Set([...prev.scrollPatterns, getCurrentSection()])]
      }));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getCurrentSection = () => {
    // Determine current section based on scroll position
    const sections = ['hero', 'services', 'about', 'contact'];
    for (const section of sections) {
      const element = document.getElementById(section);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          return section;
        }
      }
    }
    return 'unknown';
  };

  // Enhanced field tracking
  const handleFieldFocus = (fieldName) => {
    setUserInteractions(prev => ({
      ...prev,
      fieldSequence: [...prev.fieldSequence, fieldName],
      fieldFocusTimes: {
        ...prev.fieldFocusTimes,
        [fieldName]: {
          startTime: Date.now(),
          totalTime: prev.fieldFocusTimes[fieldName]?.totalTime || 0,
          focusCount: (prev.fieldFocusTimes[fieldName]?.focusCount || 0) + 1
        }
      }
    }));
  };

  const handleFieldBlur = (fieldName) => {
    setUserInteractions(prev => ({
      ...prev,
      fieldFocusTimes: {
        ...prev.fieldFocusTimes,
        [fieldName]: {
          ...prev.fieldFocusTimes[fieldName],
          totalTime: (prev.fieldFocusTimes[fieldName]?.totalTime || 0) +
            (Date.now() - (prev.fieldFocusTimes[fieldName]?.startTime || Date.now()))
        }
      }
    }));
  };

  const analyzeMessageIntent = (message) => {
    const text = message.toLowerCase();
    const intents = {
      primary: null,
      specific: [],
      technologies: [],
      industry: null
    };

    // Primary intent analysis
    if (text.includes('chatbot') || text.includes('chat bot')) {
      intents.primary = 'chatbot';
      if (text.includes('customer service') || text.includes('support')) intents.specific.push('customer-service');
      if (text.includes('sales') || text.includes('lead')) intents.specific.push('sales');
      if (text.includes('support') || text.includes('help')) intents.specific.push('support');
    } else if (text.includes('automation') || text.includes('automate')) {
      intents.primary = 'automation';
      if (text.includes('workflow')) intents.specific.push('workflow');
      if (text.includes('email')) intents.specific.push('email');
      if (text.includes('social')) intents.specific.push('social-media');
    } else if (text.includes('seo') || text.includes('search engine')) {
      intents.primary = 'seo';
      if (text.includes('local')) intents.specific.push('local');
      if (text.includes('technical')) intents.specific.push('technical');
      if (text.includes('content')) intents.specific.push('content');
    } else if (text.includes('website') || text.includes('web site')) {
      intents.primary = 'website';
      if (text.includes('new')) intents.specific.push('new-development');
      if (text.includes('redesign')) intents.specific.push('redesign');
      if (text.includes('optimize')) intents.specific.push('optimization');
    }

    // Technology detection
    if (text.includes('machine learning') || text.includes('ml')) intents.technologies.push('machine-learning');
    if (text.includes('natural language') || text.includes('nlp')) intents.technologies.push('natural-language');
    if (text.includes('computer vision') || text.includes('image')) intents.technologies.push('computer-vision');
    if (text.includes('predict') || text.includes('forecast')) intents.technologies.push('predictive-analytics');

    // Industry detection
    if (text.includes('retail') || text.includes('shop') || text.includes('store')) intents.industry = 'retail';
    if (text.includes('health') || text.includes('medical') || text.includes('hospital')) intents.industry = 'healthcare';
    if (text.includes('bank') || text.includes('finance') || text.includes('investment')) intents.industry = 'finance';
    if (text.includes('real estate') || text.includes('property')) intents.industry = 'real-estate';
    if (text.includes('education') || text.includes('school') || text.includes('university')) intents.industry = 'education';

    return intents;
  };

  const calculateLeadScore = (message, interactions) => {
    const text = message.toLowerCase();
    
    return {
      messageQuality: {
        detail: message.length > 200 ? 'high' : message.length > 100 ? 'medium' : 'low',
        specificity: {
          score: calculateSpecificityScore(text),
          hasNumbers: /\d+/.test(text),
          hasBusinessMetrics: /employees|revenue|customers|users|market share/i.test(text),
          mentionsTimeline: /asap|urgent|tomorrow|next week|month/i.test(text)
        },
        budget: {
          indicated: /budget|cost|price|investment/i.test(text),
          range: text.includes('enterprise') ? 'enterprise' : 
                 text.includes('small business') ? 'small' : 'unknown',
          keywords: extractBudgetKeywords(text)
        },
        readiness: {
          immediacy: text.includes('asap') ? 'asap' :
                    text.includes('urgent') ? 'urgent' :
                    text.includes('soon') ? 'soon' : 'exploring',
          stage: text.includes('ready to') ? 'decided' :
                 text.includes('comparing') ? 'comparing' :
                 text.includes('looking') ? 'research' : 'unknown'
        }
      },
      companySignals: {
        size: extractCompanySize(text),
        currentTech: extractTechnologyMentions(text)
      },
      engagement: {
        formBehavior: {
          fieldSequence: interactions.fieldSequence,
          editPatterns: interactions.editPatterns,
          timePatterns: {
            totalTime: Math.round((Date.now() - interactions.startTime) / 1000),
            fieldTimes: Object.entries(interactions.fieldFocusTimes).reduce((acc, [field, data]) => {
              acc[field] = Math.round(data.totalTime / 1000);
              return acc;
            }, {})
          }
        },
        pageInteraction: {
          scrollDepth: `${interactions.scrollDepth}%`,
          scrollPatterns: interactions.scrollPatterns,
          timeBeforeForm: Math.round((Date.now() - window.performance.timing.navigationStart) / 1000),
          previousPageViews: JSON.parse(localStorage.getItem('pageViews') || '[]')
        }
      }
    };
  };

  const calculateSpecificityScore = (text) => {
    let score = 0;
    // Keywords that indicate specificity
    const specificityIndicators = [
      'specific', 'exactly', 'precisely', 'particular',
      'integration', 'requirement', 'timeline', 'deadline',
      'budget', 'cost', 'roi', 'metric'
    ];
    
    specificityIndicators.forEach(indicator => {
      if (text.includes(indicator)) score += 1;
    });
    
    // Add points for numbers and dates
    if (/\d+/.test(text)) score += 2;
    if (/january|february|march|april|may|june|july|august|september|october|november|december/i.test(text)) score += 2;
    if (/monday|tuesday|wednesday|thursday|friday|saturday|sunday/i.test(text)) score += 1;
    
    return Math.min(score, 10); // Cap at 10
  };

  const extractBudgetKeywords = (text) => {
    const budgetKeywords = [];
    if (text.includes('budget')) budgetKeywords.push('budget');
    if (text.includes('investment')) budgetKeywords.push('investment');
    if (text.includes('cost')) budgetKeywords.push('cost');
    if (text.includes('pricing')) budgetKeywords.push('pricing');
    return budgetKeywords;
  };

  const extractCompanySize = (text) => {
    const employeeMatch = text.match(/(\d+)\s*(employee|employees|people|staff)/i);
    return {
      keywords: ['employees', 'team', 'company', 'enterprise'].filter(k => text.includes(k)),
      range: employeeMatch ? 
        parseInt(employeeMatch[1]) <= 10 ? '1-10' :
        parseInt(employeeMatch[1]) <= 50 ? '11-50' :
        parseInt(employeeMatch[1]) <= 200 ? '51-200' : '201+' 
        : 'unknown'
    };
  };

  const extractTechnologyMentions = (text) => {
    return {
      mentioned: [
        'crm', 'erp', 'chatbot', 'automation', 'ai'
      ].filter(tech => text.includes(tech)),
      integration: [
        'salesforce', 'sap', 'oracle', 'microsoft'
      ].filter(tech => text.includes(tech))
    };
  };

  const getGeographicContext = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hour = new Date().getHours();
    
    return {
      region: {
        timezone,
        inferredLocation: timezone.split('/')[1].replace('_', ' '),
        businessHours: hour >= 9 && hour <= 17,
        localTime: new Date().toLocaleTimeString()
      },
      market: {
        urbanDensity: timezone.includes('New_York') || timezone.includes('Los_Angeles') ? 'metropolitan' : 'unknown',
        economicRegion: timezone.includes('New_York') ? 'tech-hub' : 'unknown',
        competitorDensity: 'unknown'
      }
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setUserInteractions(prev => ({
      ...prev,
      editPatterns: {
        ...prev.editPatterns,
        totalEdits: prev.editPatterns.totalEdits + 1,
        fieldEdits: {
          ...prev.editPatterns.fieldEdits,
          [name]: (prev.editPatterns.fieldEdits[name] || 0) + 1
        }
      }
    }));
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prevState => ({
        ...prevState,
        [name]: formatted
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email && !formData.phone) {
      setSubmitStatus('error');
      return;
    }

    if (formData.phone && !isValidUSPhone(formData.phone)) {
      setSubmitStatus('phone-error');
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setSubmitStatus('email-error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    // Analyze message intent and calculate lead score
    const messageIntent = analyzeMessageIntent(formData.message);
    const leadScore = calculateLeadScore(formData.message, userInteractions);
    const geographicContext = getGeographicContext();

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone ? normalizePhoneNumber(formData.phone) : '', // Convert to XXX-XXX-XXXX format for backend
          message: formData.message,
          timestamp: new Date().toISOString(),
          source: {
            // Basic source info
            origin: window.location.origin,
            path: window.location.pathname,
            referrer: document.referrer || 'direct',
            
            // UTM parameters
            utm: {
              source: new URLSearchParams(window.location.search).get('utm_source') || null,
              medium: new URLSearchParams(window.location.search).get('utm_medium') || null,
              campaign: new URLSearchParams(window.location.search).get('utm_campaign') || null,
              term: new URLSearchParams(window.location.search).get('utm_term') || null,
              content: new URLSearchParams(window.location.search).get('utm_content') || null
            },

            // Enhanced device info
            device: {
              type: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'mobile' : 'desktop',
              browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                      navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                      navigator.userAgent.includes('Safari') ? 'Safari' : 
                      navigator.userAgent.includes('Edge') ? 'Edge' : 
                      navigator.userAgent.includes('MSIE') || navigator.userAgent.includes('Trident/') ? 'IE' : 'Other',
              screenSize: `${window.screen.width}x${window.screen.height}`,
              language: navigator.language || navigator.userLanguage,
              os: navigator.platform || 'unknown',
              touchscreen: 'ontouchstart' in window,
              cookiesEnabled: navigator.cookieEnabled
            },

            // Enhanced timing data
            timing: {
              localTime: new Date().toLocaleString(),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date().getDay()],
              hour: new Date().getHours(),
              peak: (new Date().getHours() >= 9 && new Date().getHours() <= 17) ? 'business-hours' : 'after-hours'
            },

            // Enhanced interaction data
            interaction: leadScore.engagement,

            // AI Context and Intent
            aiContext: {
              ...messageIntent,
              leadScoring: leadScore.messageQuality,
              companyContext: leadScore.companySignals
            },

            // Geographic and Market Context
            contextualData: {
              geographic: geographicContext,
              industryContext: {
                sector: {
                  primary: messageIntent.industry,
                  techMaturity: leadScore.companySignals.currentTech.mentioned.length > 2 ? 'high' : 
                              leadScore.companySignals.currentTech.mentioned.length > 0 ? 'medium' : 'low'
                },
                marketSignals: {
                  growthPhase: leadScore.messageQuality.specificity.hasBusinessMetrics ? 'expansion' : 'unknown',
                  digitalTransformation: messageIntent.technologies.length > 0 ? 'active' : 'exploring',
                  aiReadiness: messageIntent.primary === 'chatbot' || messageIntent.technologies.includes('machine-learning') ? 'high' : 'medium'
                },
                businessCycle: {
                  seasonal: false,
                  peakPeriod: new Date().getMonth() < 3 ? 'Q1' : 
                             new Date().getMonth() < 6 ? 'Q2' : 
                             new Date().getMonth() < 9 ? 'Q3' : 'Q4',
                  budgetCycle: leadScore.messageQuality.budget.indicated ? 'active' : 'unknown'
                }
              }
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Update visit tracking
      const visitCount = parseInt(localStorage.getItem('visitCount') || '0') + 1;
      localStorage.setItem('visitCount', visitCount.toString());
      localStorage.setItem('returningUser', 'true');
      localStorage.setItem('lastVisit', new Date().toISOString());

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      onSuccess(); // Call the success handler from parent
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-content">
          <span className="close" onClick={onClose}>&times;</span>
          <h2>Contact Us</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => handleFieldFocus('name')}
                onBlur={() => handleFieldBlur('name')}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => handleFieldFocus('email')}
                onBlur={() => handleFieldBlur('email')}
                placeholder="Enter email and/or phone below"
              />
              {formData.email && !isValidEmail(formData.email) && (
                <p className="error-message">Please enter a valid email address</p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone:</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onFocus={() => handleFieldFocus('phone')}
                onBlur={() => handleFieldBlur('phone')}
                placeholder="(XXX) XXX-XXXX"
              />
              {formData.phone && !isValidUSPhone(formData.phone) && (
                <p className="error-message">Please enter a valid phone number</p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="message">Message:</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onFocus={() => handleFieldFocus('message')}
                onBlur={() => handleFieldBlur('message')}
                required
              />
            </div>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitStatus === 'success' && (
              <p className="success-message">Message sent successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="error-message">Please provide either an email or phone number.</p>
            )}
            {submitStatus === 'phone-error' && (
              <p className="error-message">Please enter a valid phone number in (XXX) XXX-XXXX format.</p>
            )}
            {submitStatus === 'email-error' && (
              <p className="error-message">Please enter a valid email address.</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default App
